// Vistas de casos (D34), como las "Overviews" de Zammad: un nombre y sus condiciones. Se guardan en el objeto
// vistaCaso de la app; aqui se convierten en el filtro GraphQL del motor para contar y listar en el servidor (asi una
// vista de cerrados no carga miles de casos en el navegador).
import type { EstadoCaso } from '../api/casos';
import { ESTADOS_FILTRO, type EstadoFiltro, type QuienFiltro } from './senalesCaso';

export type Asignado = '' | 'yo' | 'nadie';
export type VistaCaso = {
  id: string;
  nombre: string;
  orden: number;
  estados: EstadoCaso[];
  grupos: string[];
  gruposExcluidos: string[];
  etiquetas: string[];
  asignado: Asignado;
  tituloContiene: string;
  miembros: string[];
  activa: boolean;
};

export const ESTADOS_ABIERTOS: EstadoCaso[] = ['NUEVO', 'ABIERTO', 'PENDIENTE'];

// Quien ve la vista: la lista de miembros vacia es "todos".
export const vistaVisible = (v: Pick<VistaCaso, 'miembros' | 'activa'>, miembroId: string | undefined) =>
  v.activa && (!v.miembros.length || (!!miembroId && v.miembros.includes(miembroId)));

// `gruposPermitidos`: solo casos de esos grupos (los grupos activos de la persona; el administrador, todos los activos).
// `estado` y `quien` son los filtros de la barra; `huerfanos`: casos cuyo grupo no es ninguno de `gruposExistentes`
// (los grupos activos del CRM).
// `ids`: lo que encontro el buscador para `q` (D45); sin el, `q` se busca solo en asunto, correo y numero.
export type Ctx = { miembroId?: string; grupo?: string; q?: string; ids?: string[]; gruposPermitidos?: string[]; gruposExistentes?: string[]; estado?: EstadoFiltro; quien?: QuienFiltro; huerfanos?: boolean };

const NINGUNO = '00000000-0000-0000-0000-000000000000';

// Filtro del motor para una vista. `grupo` es el filtro de la lista (Todos / un grupo); `q` la busqueda.
export function filtroDeVista(v: Omit<VistaCaso, 'id' | 'nombre' | 'orden' | 'miembros' | 'activa'>, ctx: Ctx = {}) {
  const y: Record<string, unknown>[] = [];
  if (v.estados.length) y.push({ estado: { in: v.estados } });
  if (v.grupos.length) y.push({ grupo: { in: v.grupos } });
  if (v.gruposExcluidos.length) y.push({ not: { grupo: { in: v.gruposExcluidos } } });
  if (v.etiquetas.length) y.push({ or: v.etiquetas.map((e) => ({ etiquetas: { containsIlike: `%${e}%` } })) });
  if (v.asignado === 'yo') y.push({ asignadoId: { eq: ctx.miembroId ?? '00000000-0000-0000-0000-000000000000' } });
  if (v.asignado === 'nadie') y.push({ asignadoId: { is: 'NULL' } });
  if (v.tituloContiene.trim()) y.push({ name: { ilike: `%${v.tituloContiene.trim()}%` } });
  if (ctx.huerfanos) y.push({ or: [{ not: { grupo: { in: ctx.gruposExistentes?.length ? ctx.gruposExistentes : ['—'] } } }, { grupo: { is: 'NULL' } }] });
  else if (ctx.grupo) y.push({ grupo: { eq: ctx.grupo } });
  else if (ctx.gruposPermitidos) y.push({ grupo: { in: ctx.gruposPermitidos.length ? ctx.gruposPermitidos : ['—'] } });
  if (ctx.estado) y.push({ estado: { in: ESTADOS_FILTRO[ctx.estado] } });
  if (ctx.quien === 'mios') y.push({ asignadoId: { eq: ctx.miembroId ?? '00000000-0000-0000-0000-000000000000' } });
  if (ctx.quien === 'sintomar') y.push({ asignadoId: { is: 'NULL' } });
  const q = ctx.q?.trim();
  if (q && ctx.ids) y.push({ id: { in: ctx.ids.length ? ctx.ids : [NINGUNO] } });
  else if (q) {
    const n = Number(q.replace(/^#/, ''));
    y.push({ or: [{ name: { ilike: `%${q}%` } }, { correoCliente: { ilike: `%${q}%` } }, ...(Number.isFinite(n) && n > 0 ? [{ numero: { eq: n } }] : [])] });
  }
  return y.length ? { and: y } : {};
}

// Resumen legible de las condiciones (Administracion -> Vistas de casos).
export function describirVista(v: VistaCaso, nombreMiembro: (id: string) => string) {
  const p: string[] = [];
  p.push(v.estados.length ? v.estados.map((e) => e.toLowerCase()).join(', ') : 'todos los estados');
  if (v.grupos.length) p.push(`grupo ${v.grupos.join(', ')}`);
  if (v.gruposExcluidos.length) p.push(`sin ${v.gruposExcluidos.join(', ')}`);
  if (v.etiquetas.length) p.push(`etiqueta ${v.etiquetas.join(' o ')}`);
  if (v.asignado === 'yo') p.push('asignados a quien la mira');
  if (v.asignado === 'nadie') p.push('sin asignar');
  if (v.tituloContiene) p.push(`asunto con "${v.tituloContiene}"`);
  p.push(v.miembros.length ? `la ven ${v.miembros.map(nombreMiembro).join(', ')}` : 'la ven todos');
  return p.join(' · ');
}
